import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { ASSETS, IMAGES } from "@/lib/site";

const KUNST_META = [
    { title: "River-Table", sub: "Massivholz mit türkisem Epoxidharz-Fluss, Baumkante und Stahlgestell." },
    { title: "Frisch aus der Werkstatt", sub: "Massive Tischplatte aus heimischem Holz — hier entsteht Ihr Unikat." },
    { title: "Angekommen", sub: "Fertiger Esstisch beim Kunden zu Hause." },
    { title: "Wurzel-Beistelltisch", sub: "Ein Stück Natur — gewachsenes Wurzelholz als Tisch." },
    { title: "„Willkommen kleines Wunder“", sub: "Schild zur Geburt — Motiv, Symbole und Text ganz nach Ihrem Wunsch." },
    { title: "Wurzel-Vase", sub: "Naturform als Vase — kein Stück wie das andere." },
    { title: "„Große Träume …“", sub: "Wandbild mit Spruch und Symbol — z. B. zur Hochzeit oder zum Jubiläum." },
    { title: "Baumscheiben-Brett", sub: "Servier- und Schneidebrett mit natürlicher Rindenkante." },
];

const CATS = [
    {
        key: "holzkunst",
        label: "Holzkunst",
        images: ASSETS.kunst.map((src, i) => ({ src, ...KUNST_META[i] })),
        caption: "Massivholz-Tische & Unikate",
        text: "Massivholz-Tische, River-Tables mit Epoxidharz, Schilder und besondere Einzelstücke — jedes Werk handgefertigt aus heimischem Holz. Beschriftungen und Symbole passen wir individuell an Ihren Anlass an — ob Geburt, Hochzeit, Jubiläum oder einfach Ihr Name.",
    },
    {
        key: "brennholz",
        label: "Brennholz",
        images: [{ src: IMAGES.brennholz }],
        caption: "Ofenfertig gestapelt",
        text: "Scheitlänge 20–60 cm (nach Absprache) — geschnitten, gespalten und sauber gesetzt, lieferbereit.",
    },
    {
        key: "bauholz",
        label: "Bauholz",
        images: [{ src: IMAGES.bauholz }],
        caption: "Bohlen & Balken",
        text: "Sägeraues Bauholz aus heimischen Wäldern — für Carports, Hochbeete und vieles mehr.",
    },
];

export const Galerie = () => {
    const [active, setActive] = useState(CATS[0]);
    const [imgIdx, setImgIdx] = useState(0);

    const selectCat = (c) => {
        setActive(c);
        setImgIdx(0);
    };

    useEffect(() => {
        const onSelect = (e) => {
            const cat = CATS.find((c) => c.key === e.detail);
            if (cat) selectCat(cat);
        };
        window.addEventListener("gallery:select", onSelect);
        return () => window.removeEventListener("gallery:select", onSelect);
    }, []);

    return (
        <section id="galerie" className="border-y border-[#2E2517] bg-[#1B150E]" data-testid="galerie-section">
            <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
                <Reveal>
                    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Galerie</p>
                    <h2 className="mb-10 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
                        Holz, das man <span className="text-[#C88A45]">sehen muss.</span>
                    </h2>
                </Reveal>
                <div className="mb-10 flex flex-wrap gap-3" role="tablist" aria-label="Galerie-Kategorien">
                    {CATS.map((c) => (
                        <button
                            key={c.key}
                            role="tab"
                            aria-selected={active.key === c.key}
                            data-testid={`gallery-tab-${c.key}`}
                            onClick={() => selectCat(c)}
                            className={`rounded-full border px-5 py-2 text-sm font-semibold transition-[border-color,color,background-color] duration-200 ${
                                active.key === c.key
                                    ? "border-[#C88A45] bg-[#C88A45]/10 text-[#C88A45]"
                                    : "border-[#2E2517] text-[#9C917D] hover:border-[#C88A45]/40 hover:text-[#EDE6DA]"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="relative overflow-hidden rounded border border-[#2E2517]" data-testid="gallery-image-frame">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={`${active.key}-${imgIdx}`}
                                    src={active.images[imgIdx].src}
                                    alt={`${active.label} — ${active.images[imgIdx].title || active.caption}`}
                                    initial={{ opacity: 0, scale: 1.04 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                    className="img-warm h-72 w-full object-cover sm:h-[28rem]"
                                />
                            </AnimatePresence>
                            <span className="absolute bottom-4 left-4 rounded-full bg-[#120E09]/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#C88A45] backdrop-blur-sm">
                                {active.caption}
                            </span>
                        </div>
                        {active.images[imgIdx].title && (
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${active.key}-caption-${imgIdx}`}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.35 }}
                                    className="mt-4"
                                    data-testid="gallery-image-caption"
                                >
                                    <p className="font-display text-lg font-bold uppercase tracking-tight text-[#EDE6DA]">
                                        {active.images[imgIdx].title}
                                    </p>
                                    <p className="mt-1 text-sm leading-relaxed text-[#9C917D]">{active.images[imgIdx].sub}</p>
                                </motion.div>
                            </AnimatePresence>
                        )}
                        {active.images.length > 1 && (
                            <div className="mt-4 flex gap-3" data-testid="gallery-thumbs">
                                {active.images.map((img, i) => (
                                    <button
                                        key={img.src}
                                        data-testid={`gallery-thumb-${i}`}
                                        onClick={() => setImgIdx(i)}
                                        aria-label={`${img.title || `Bild ${i + 1}`} anzeigen`}
                                        className={`h-16 w-24 overflow-hidden rounded border transition-[border-color,opacity] duration-200 ${
                                            i === imgIdx ? "border-[#C88A45]" : "border-[#2E2517] opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        <img src={img.src} alt="" className="img-warm h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col justify-center lg:col-span-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.key}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-[#EDE6DA]">{active.label}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-[#9C917D]">{active.text}</p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
